import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, CheckCircle2, XCircle, UserPlus, Clock, Shield, ShieldCheck, X, FileText } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';
import { useAuth } from '../../context/AuthContext';

const COUNTRIES = ['IN', 'UK', 'MY', 'GENERIC'];
const ROLES = ['staff', 'hr', 'accounts', 'content_publisher', 'admin', 'super_admin'];
const CONTRACTS = ['permanent', 'contract', 'intern', 'consultant'];

const api = {
  list: (status) => apiClient.get(`/hr/onboarding${status ? `?status=${status}` : ''}`).then((r) => r.data),
  counts: () => apiClient.get('/hr/onboarding/counts').then((r) => r.data),
  hrApprove: (id, body) => apiClient.post(`/hr/onboarding/${id}/hr-approve`, body).then((r) => r.data),
  adminApprove: (id, body) => apiClient.post(`/hr/onboarding/${id}/admin-approve`, body).then((r) => r.data),
  reject: (id, reason) => apiClient.post(`/hr/onboarding/${id}/reject`, { reason }).then((r) => r.data),
};

const STATUS_META = {
  pending_hr: { label: 'Pending HR review', color: 'bg-amber-100 text-amber-800', icon: Shield },
  pending_admin: { label: 'Pending Admin sign-off', color: 'bg-blue-100 text-blue-800', icon: ShieldCheck },
  created: { label: 'Onboarded', color: 'bg-emerald-100 text-emerald-800', icon: CheckCircle2 },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-800', icon: XCircle },
};

const OnboardingPage = () => {
  const { user } = useAuth();
  const role = user?.role;
  const isAdmin = role === 'super_admin' || role === 'admin';
  const [tab, setTab] = useState('pending_hr');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.list(tab === 'all' ? null : tab); setItems(r.data || []); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const tabs = useMemo(() => {
    const t = [
      ['pending_hr', 'Pending HR', role === 'hr' || isAdmin],
      ['pending_admin', 'Pending Admin', isAdmin],
      ['created', 'Onboarded', true],
      ['rejected', 'Rejected', true],
      ['all', 'All', true],
    ];
    return t.filter(([, , show]) => show).map(([k, l]) => [k, l]);
  }, [role, isAdmin]);

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-testid="onboarding-page">
      <div className="mb-4 sm:mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase">Onboarding</span>
        <h1 className="mt-2 text-3xl sm:text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Hires awaiting approval</h1>
        <p className="mt-1 text-xs sm:text-sm text-[#0b1a2e]/60">Two-step sign-off: HR reviews → Admin finalises → user + HR profile is created automatically.</p>
      </div>

      <div className="flex border-b border-black/5 bg-[#f7fbf8] rounded-t-2xl overflow-x-auto mb-4">
        {tabs.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`onboard-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-medium whitespace-nowrap ${tab === k ? 'text-[#0f5c3d] bg-white border-b-2 border-[#0f5c3d]' : 'text-[#0b1a2e]/60 hover:text-[#0b1a2e]'}`}>
            {l}
          </button>
        ))}
      </div>

      <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
        {loading ? <div className="p-10 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline" /></div> :
          items.length === 0 ? <div className="p-10 text-center text-sm text-[#0b1a2e]/50">No requests in this queue.</div> :
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
              <tr>
                <th className="text-left p-3">Candidate</th>
                <th className="text-left p-3">Role · Country</th>
                <th className="text-left p-3">Submitted</th>
                <th className="text-left p-3">Status</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const meta = STATUS_META[r.status] || STATUS_META.pending_hr;
                const Icon = meta.icon;
                return (
                  <tr key={r.id} className="border-t border-black/5 hover:bg-[#f7fbf8]/50" data-testid={`onboard-row-${r.id}`}>
                    <td className="p-3">
                      <div className="font-medium text-[#0b1a2e]">{r.name}</div>
                      <div className="text-xs text-[#0b1a2e]/60">{r.email}</div>
                    </td>
                    <td className="p-3 text-xs">
                      <span className="font-mono">{r.role}</span> · {r.country || '—'} · {r.designation || 'no title'}
                    </td>
                    <td className="p-3 text-xs text-[#0b1a2e]/60">
                      <div>{new Date(r.submitted_at).toLocaleDateString()}</div>
                      <div className="text-[10px]">by {r.submitted_by_name}</div>
                    </td>
                    <td className="p-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-widest ${meta.color}`}>
                        <Icon className="w-3 h-3" /> {meta.label}
                      </span>
                    </td>
                    <td className="p-3 text-right">
                      <button onClick={() => setActive(r)} data-testid={`onboard-review-${r.id}`}
                        className="px-3 py-1.5 rounded-full bg-[#0f5c3d] text-white text-xs font-semibold hover:bg-[#0b4a30]">
                        Review
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        }
      </div>

      {active && <ReviewModal item={active} isAdmin={isAdmin} onClose={() => setActive(null)} onDone={() => { setActive(null); load(); }} />}
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 mb-1 font-semibold">{label}</label>
    {children}
  </div>
);

const ReviewModal = ({ item, isAdmin, onClose, onDone }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({ ...item });
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const canHrApprove = item.status === 'pending_hr' && ['super_admin', 'admin', 'hr'].includes(user?.role);
  const canAdminApprove = item.status === 'pending_admin' && isAdmin;
  const canReject = ['pending_hr', 'pending_admin'].includes(item.status)
    && (['super_admin', 'admin'].includes(user?.role) || (item.status === 'pending_hr' && user?.role === 'hr'));
  const readOnly = !canHrApprove && !canAdminApprove;

  const amend = () => {
    // Return only fields that changed vs the incoming record
    const changed = {};
    ['name', 'email', 'role', 'department_id', 'job_title_id', 'country', 'hire_date',
      'employee_code', 'designation', 'contract_type', 'send_invite'].forEach((k) => {
      if (form[k] !== undefined && form[k] !== item[k]) changed[k] = form[k];
    });
    if (note) changed.note = note;
    return changed;
  };

  const doAction = async (kind) => {
    setBusy(true);
    try {
      if (kind === 'hr') await api.hrApprove(item.id, amend());
      else if (kind === 'admin') await api.adminApprove(item.id, amend());
      else {
        if (!reason.trim()) { toast({ title: 'Please state a reason', variant: 'destructive' }); setBusy(false); return; }
        await api.reject(item.id, reason.trim());
      }
      const done = { hr: 'HR approval recorded — sent to Admin', admin: 'Onboarded — user account created', reject: 'Request rejected' }[kind];
      toast({ title: done });
      onDone();
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Action failed', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="onboard-review-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-black/5 flex items-center justify-between bg-[#0b1a2e] text-white">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#7bbf95] font-semibold">Onboarding review</div>
            <div className="text-lg font-semibold flex items-center gap-2"><UserPlus className="w-5 h-5" /> {item.name}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Full name"><input disabled={readOnly} value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Email"><input disabled={readOnly} value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Role">
              <select disabled={readOnly} value={form.role || 'staff'} onChange={(e) => setForm({ ...form, role: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Country">
              <select disabled={readOnly} value={form.country || ''} onChange={(e) => setForm({ ...form, country: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white">
                <option value="">—</option>{COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Hire date"><input disabled={readOnly} type="date" value={(form.hire_date || '').slice(0, 10)} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Employee code"><input disabled={readOnly} value={form.employee_code || ''} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Designation"><input disabled={readOnly} value={form.designation || ''} onChange={(e) => setForm({ ...form, designation: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm" /></Field>
            <Field label="Contract">
              <select disabled={readOnly} value={form.contract_type || 'permanent'} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white">
                {CONTRACTS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          {!readOnly && (
            <Field label="Approval note (optional)">
              <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="onboard-note"
                className="w-full px-3 py-2 border border-slate-300 rounded text-sm" placeholder="Optional context for the audit trail" />
            </Field>
          )}

          {canReject && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-red-800 font-semibold mb-1">Reject with reason</div>
              <input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="onboard-reject-reason"
                placeholder="Why is this request being rejected?" className="w-full px-3 py-2 border border-red-300 rounded text-sm bg-white" />
            </div>
          )}

          <div className="bg-[#f7fbf8] rounded-lg p-3">
            <div className="text-[10px] uppercase tracking-widest text-[#0b1a2e]/60 font-semibold mb-2 flex items-center gap-1"><Clock className="w-3 h-3" /> Timeline</div>
            <div className="space-y-1 text-xs">
              {(item.timeline || []).map((t, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className="text-[#0f5c3d] font-medium">{t.action.replace(/_/g, ' ')}</span>
                  <span className="text-[#0b1a2e]/60">— {t.by_name} · {new Date(t.at).toLocaleString()}</span>
                  {t.note && <span className="text-[#0b1a2e]/50 italic">"{t.note}"</span>}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-black/5 bg-[#f7fbf8] flex justify-end gap-2 flex-wrap">
          <button onClick={onClose} className="px-4 py-2 rounded-lg text-sm hover:bg-black/5">Close</button>
          {canReject && (
            <button onClick={() => doAction('reject')} disabled={busy || !reason.trim()} data-testid="onboard-reject-btn"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold disabled:opacity-60">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <XCircle className="w-4 h-4" />} Reject
            </button>
          )}
          {canHrApprove && (
            <button onClick={() => doAction('hr')} disabled={busy} data-testid="onboard-hr-approve-btn"
              className="inline-flex items-center gap-1 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-semibold disabled:opacity-60 hover:bg-blue-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />} HR approve
            </button>
          )}
          {canAdminApprove && (
            <button onClick={() => doAction('admin')} disabled={busy} data-testid="onboard-admin-approve-btn"
              className="inline-flex items-center gap-1 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-60 hover:bg-[#0b4a30]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Admin approve & create user
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
